import { BigNumber } from 'ethers'
import React from 'react'
import styled from 'styled-components'
import { CurrencySymbolImage, Spinner } from '../../common'
import { Borders, Colors, Fonts, FontWeights } from '../../constants'
import { ExchangeQuote } from '../../hooks'
import { Currency } from '../../model'
import { Exchange } from './Exchange'
import { RefreshButton } from './RefreshButton'
import { RefreshCountdown } from './RefreshCountdown'
import { usePairSelectModal } from '../../providers'

interface Props {
  controlled: 'first' | 'second'
  currencyIn: Currency
  currencyOut: Currency
  firstValue: string
  secondValue: string
  quotes?: ExchangeQuote[]
  timeoutStartAt: Date | null
  autoRefresh: boolean
  toggleAutoRefresh: () => void
  refresh: () => void
}

export function Comparator({
  controlled,
  currencyIn,
  currencyOut,
  firstValue,
  secondValue,
  quotes,
  timeoutStartAt,
  autoRefresh,
  toggleAutoRefresh,
  refresh,
}: Props) {
  const selling = controlled === 'first'
  const firstSymbol = selling ? currencyIn.symbol : currencyOut.symbol
  const secondSymbol = selling ? currencyOut.symbol : currencyIn.symbol
  const connector = selling ? 'for' : 'with'

  const { bestQuote, worstQuote } = getExtremeQuotes(quotes)
  const maxGasUsd = getMaxGasUsd(quotes)

  const { togglePairSelectModal } = usePairSelectModal()

  let display
  if (quotes) {
    display = quotes.map((quote, i) => (
      <Exchange
        key={i}
        currencyIn={currencyIn}
        currencyOut={currencyOut}
        quote={quote}
        rank={i}
        maxGasUsd={maxGasUsd}
        bestQuote={bestQuote}
        worstQuote={worstQuote}
      />
    ))
  } else if (firstValue !== '' && secondValue !== '') {
    display = (
      <tbody>
        <tr>
          <td colSpan={4}>
            <Loading>
              <Spinner />
            </Loading>
          </td>
        </tr>
      </tbody>
    )
  } else {
    display = (
      <tbody>
        <tr>
          <td colSpan={4}>
            <Prompt>Input your desired trade amount and we'll show you the exchanges with the best price.</Prompt>
          </td>
        </tr>
      </tbody>
    )
  }

  return (
    <Container>
      <Header>
        <Direction>{selling ? 'Selling' : 'Buying'}</Direction>
        {selling ? firstValue : secondValue}
        <Token onClick={togglePairSelectModal}>
          <CurrencySymbolImageStyled symbol={firstSymbol} />
          {firstSymbol}
        </Token>
        {connector}
        <Token onClick={togglePairSelectModal}>
          <CurrencySymbolImageStyled symbol={secondSymbol} />
          {secondSymbol}
        </Token>
      </Header>
      <Body>
        <Table>
          <thead>
            <tr>
              <th colSpan={2}>Exchange</th>
              <th>{selling ? 'You Receive' : 'You Pay'}</th>
              <th>Diff</th>
            </tr>
          </thead>
          {display}
        </Table>
      </Body>
      <Footer>
        <RefreshControls>
          <RefreshCountdown timeoutStartAt={timeoutStartAt} autoRefresh={autoRefresh} onClick={toggleAutoRefresh} />
          <RefreshButton onClick={refresh} />
        </RefreshControls>
        <Key>
          <Savings>Savings</Savings>
          <Cost>Trade Cost</Cost>
          <Gas>Gas</Gas>
        </Key>
      </Footer>
    </Container>
  )
}

function getExtremeQuotes(quotes?: ExchangeQuote[]) {
  if (quotes && quotes.length > 0) {
    return {
      bestQuote: quotes[0],
      worstQuote: quotes[quotes.length - 1],
    }
  }
  return {}
}

function getMaxGasUsd(quotes?: ExchangeQuote[]) {
  if (quotes) {
    return quotes.reduce((maxGasUsd, quote) => {
      const gasUsd = quote.quote.gasUsd
      return gasUsd.gt(maxGasUsd) ? gasUsd : maxGasUsd
    }, BigNumber.from(0))
  }
  return BigNumber.from(0)
}

const Container = styled.div`
  margin-right: 30px;
  max-width: 550px;
  width: 100%;
  color: ${Colors.White};
  font-size: 15px;
  background-color: #272b31;
  border: 1px solid #5c5c5c;
  border-radius: ${Borders.Radius};
  box-shadow: 0 5px 20px 0 rgba(0, 0, 0, 0.1);

  @media (max-width: 1024px) {
    margin: 30px auto 0 auto;
    max-width: 400px;
  }
`

const Header = styled.div`
  padding: 0 20px;
  display: flex;
  height: 58px;
  font-weight: ${FontWeights.Semibold};
  align-items: center;
  border-bottom: 1px solid #1f1f1f;
`

const Direction = styled.span`
  margin-right: 10px;
  font-size: 20px;
  font-family: ${Fonts.Header};
  text-transform: uppercase;
`

const Token = styled.span`
  margin: auto 5px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
`

const CurrencySymbolImageStyled = styled(CurrencySymbolImage)`
  margin-right: 5px;
`

const Body = styled.div`
  border-top: 1px solid #5c5c5c;
`

const Table = styled.table`
  width: 100%;
  border-spacing: 0;
  border-collapse: collapse;

  thead {
    th {
      padding: 12px 10px;
      color: ${Colors.Gray};
      font-size: 13px;
      text-align: left;
      text-transform: uppercase;

      &:first-child {
        padding-left: 20px;
      }

      &:last-child {
        padding-right: 20px;
        text-align: right;
      }
    }
  }

  @media (max-width: 1024px) {
    thead {
      th {
        font-size: 12px;
      }
    }
  }
`

const Footer = styled.div`
  padding: 0 20px 5px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const RefreshControls = styled.div`
  display: flex;

  button {
    margin-right: 10px;
  }
`

const Key = styled.ul`
  display: flex;
  color: ${Colors.Gray};
  font-size: 12px;
  align-items: center;
  justify-content: flex-end;
  list-style: none;
  text-transform: uppercase;

  li {
    display: flex;
    align-items: center;

    &:before {
      margin-right: 5px;
      display: inline-block;
      width: 10px;
      height: 5px;
      content: '';
    }
  }

  @media (max-width: 1024px) {
    font-size: 10px;
  }
`

const Savings = styled.li`
  margin-right: 20px;

  &:before {
    background-color: #43d796;
    border-top: 1px solid #8efacb;
    border-bottom: 1px solid #8efacb;
  }
`

const Cost = styled.li`
  margin-right: 20px;

  &:before {
    background-color: #4b3657;
    border-top: 1px solid #824691;
    border-bottom: 1px solid #824691;
  }
`

const Gas = styled.li`
  &:before {
    background-color: #504b3d;
    border-top: 1px solid #8d7b4f;
    border-bottom: 1px solid #8d7b4f;
  }
`

const Loading = styled.div`
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Prompt = styled.small`
  padding: 20px 40px;
  display: block;
  color: ${Colors.LightGray};
  text-align: center;
`
